import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

interface WebModalProps {
  visible?: boolean;
  transparent?: boolean;
  animationType?: 'none' | 'slide' | 'fade';
  onRequestClose?: () => void;
  children?: React.ReactNode;
}

const Modal: React.FC<WebModalProps> = ({ visible = false, onRequestClose, children }) => {
  useEffect(() => {
    if (!visible || !onRequestClose) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [visible, onRequestClose]);

  if (!visible) return null;

  // Portaling straight to document.body (via a plain div, not a react-native-web
  // View) sidesteps two problems at once: react-native-web's View defaults to
  // position:relative in its base stylesheet, which can beat an inline
  // position:fixed override depending on CSS ordering — and even with fixed
  // positioning working, an ancestor's overflow/stacking context could still
  // clip or misplace the overlay. A body-level portal is immune to both.
  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex' }}>
      {children}
    </div>,
    document.body,
  );
};

export default Modal;
